import React, { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ThemeToggle from './ThemeToggle';
import './Navbar.css';

const Navbar = () => {
  const { user, logout, isAuthenticated, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Prevent body scroll when mobile menu is open
  // iOS Safari requires position:fixed + saving/restoring scroll position
  useEffect(() => {
    if (mobileMenuOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
      }
    }
    return () => {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
      }
    };
  }, [mobileMenuOpen]);

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
    navigate('/');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <nav className="navbar">
      {/* NYC skyline + taxi scene — decorative background */}
      <div className="navbar-scene" aria-hidden="true">
        <svg className="navbar-skyline" viewBox="0 0 1200 200" preserveAspectRatio="none" fill="none">
          {/* NYC skyline — the authentic character is flat-topped skyscrapers
              with occasional thin antennas and pencil supertalls.
              Pyramidal/stepped silhouettes are deliberately avoided because
              they read as Egyptian pyramids at low opacity.
              Features: 1 WTC obelisk + antenna, Empire State w/ thin mast,
              pencil towers (111 W 57th, 53W53 style), 432 Park's flat rect,
              Bank of America spire. */}
          <path d="M0,200 V140 H30 V120 H65 V145 H95 V115 H130 V135 H160 V60 H195 V15 H205 V8 H210 V0 H211 V8 H212 V15 H220 V60 H235 V100 H265 V130 H300 V90 H335 V115 H370 V75 H410 V40 H425 V30 H460 V2 H462 V30 H464 V40 H475 V80 H510 V120 H555 V10 H560 V6 H562 V10 H565 V120 H590 V95 H625 V130 H665 V25 H670 V15 H685 V25 H700 V135 H740 V105 H780 V85 H815 V130 H855 V55 H880 V35 H920 V10 H925 V3 H927 V10 H930 V35 H935 V55 H945 V100 H980 V135 H1020 V105 H1055 V50 H1085 V30 H1125 V5 H1128 V30 H1130 V50 H1140 V90 H1175 V10 H1180 V5 H1183 V10 H1186 V90 H1190 V140 H1200 V200 Z" fill="currentColor" />
        </svg>

        {/* Taxi 1 — foreground, drives right */}
        <svg className="navbar-taxi nb-taxi-1" viewBox="0 0 120 50" fill="none">
          <rect x="10" y="16" width="95" height="22" rx="4" fill="var(--taxi-yellow)" />
          <rect x="28" y="4" width="52" height="18" rx="4" fill="var(--taxi-yellow-dark)" />
          <rect x="32" y="7" width="15" height="10" rx="2" fill="var(--bg-primary)" opacity="0.35" />
          <rect x="51" y="7" width="15" height="10" rx="2" fill="var(--bg-primary)" opacity="0.35" />
          <circle cx="32" cy="42" r="8" fill="var(--text-muted)" />
          <circle cx="32" cy="42" r="4" fill="var(--bg-muted)" />
          <circle cx="88" cy="42" r="8" fill="var(--text-muted)" />
          <circle cx="88" cy="42" r="4" fill="var(--bg-muted)" />
          <rect x="0" y="24" width="10" height="4" rx="2" fill="var(--taxi-yellow-light)" opacity="0.7" />
          <rect x="105" y="26" width="10" height="3" rx="1.5" fill="var(--danger)" opacity="0.5" />
        </svg>

        {/* Taxi 2 — midground, drives left */}
        <svg className="navbar-taxi nb-taxi-2" viewBox="0 0 120 50" fill="none">
          <rect x="10" y="16" width="95" height="22" rx="4" fill="var(--taxi-yellow)" />
          <rect x="28" y="4" width="52" height="18" rx="4" fill="var(--taxi-yellow-dark)" />
          <rect x="32" y="7" width="15" height="10" rx="2" fill="var(--bg-primary)" opacity="0.35" />
          <rect x="51" y="7" width="15" height="10" rx="2" fill="var(--bg-primary)" opacity="0.35" />
          <circle cx="32" cy="42" r="8" fill="var(--text-muted)" />
          <circle cx="32" cy="42" r="4" fill="var(--bg-muted)" />
          <circle cx="88" cy="42" r="8" fill="var(--text-muted)" />
          <circle cx="88" cy="42" r="4" fill="var(--bg-muted)" />
        </svg>
      </div>

      <div className="navbar-container">
        <div className="navbar-left">
          {location.pathname !== '/' && (
            <Link to={isAuthenticated ? (isAdmin ? '/admin' : '/dashboard') : '/'} className="navbar-logo" onClick={() => window.scrollTo(0, 0)}>
              <img src="/logo2.png" alt="Taxify.nyc" className="navbar-logo-img" />
            </Link>
          )}
        </div>

        {/* Desktop Navigation */}
        <div className="navbar-links navbar-desktop">
          {isAuthenticated ? (
            <>
              {!isAdmin && <NavLink to="/dashboard" end className="nav-link nav-link-page">Dashboard</NavLink>}
              {!isAdmin && <NavLink to="/medallions" className="nav-link nav-link-page">Medallions</NavLink>}
              {isAdmin && <NavLink to="/admin" end className="nav-link nav-link-page nav-link-admin">Admin Dashboard</NavLink>}
              <button className="nav-user-btn" onClick={() => navigate('/account')}>
                <span className="nav-user-name">{user?.name}</span>
                <svg className="nav-user-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"></circle>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                </svg>
              </button>
              <ThemeToggle />
              <button onClick={handleLogout} className="nav-button">Logout</button>
            </>
          ) : (
            <>
              <Link to="/login" className="nav-link">Sign In</Link>
              <Link to="/register" className="nav-link nav-link-primary">Register</Link>
              <ThemeToggle />
            </>
          )}
        </div>

        {/* Menu Controls */}
        <div className="navbar-mobile-controls">
          <button
            className={`hamburger-btn ${mobileMenuOpen ? 'open' : ''}`}
            onClick={toggleMobileMenu}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
          >
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
          </button>
        </div>

        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)} />
        )}

        {/* Mobile Menu */}
        <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
          {isAuthenticated ? (
            <>
              <div className="mobile-menu-user">
                <div className="mobile-user-avatar">
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
                <div className="mobile-user-info">
                  <span className="mobile-user-name">{user?.name}</span>
                  <span className="mobile-user-role">{user?.role}</span>
                </div>
              </div>

              <div className="mobile-menu-links">
                {!isAdmin && (
                  <>
                    <NavLink to="/dashboard" end className="mobile-nav-link">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="7" height="7"></rect>
                        <rect x="14" y="3" width="7" height="7"></rect>
                        <rect x="14" y="14" width="7" height="7"></rect>
                        <rect x="3" y="14" width="7" height="7"></rect>
                      </svg>
                      Dashboard
                    </NavLink>
                    <NavLink to="/medallions" className="mobile-nav-link">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <text x="12" y="16" textAnchor="middle" fontSize="12" fill="currentColor" stroke="none" fontWeight="bold">M</text>
                      </svg>
                      Medallions
                    </NavLink>
                  </>
                )}
                {isAdmin && (
                  <NavLink to="/admin" end className="mobile-nav-link mobile-nav-link-admin">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 20h9"></path>
                      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                    </svg>
                    Admin Dashboard
                  </NavLink>
                )}
                <NavLink to="/account" className="mobile-nav-link">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3"></circle>
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                  </svg>
                  Account Settings
                </NavLink>
                <div className="mobile-theme-toggle">
                  <ThemeToggle />
                </div>
                <button onClick={handleLogout} className="mobile-nav-link mobile-logout-link">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                    <polyline points="16 17 21 12 16 7"></polyline>
                    <line x1="21" y1="12" x2="9" y2="12"></line>
                  </svg>
                  Logout
                </button>
              </div>
            </>
          ) : (
            <div className="mobile-menu-links">
              <Link to="/login" className="mobile-nav-link">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
                  <polyline points="10 17 15 12 10 7"></polyline>
                  <line x1="15" y1="12" x2="3" y2="12"></line>
                </svg>
                Sign In
              </Link>
              <Link to="/register" className="mobile-nav-link mobile-nav-link-primary">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="8.5" cy="7" r="4"></circle>
                  <line x1="20" y1="8" x2="20" y2="14"></line>
                  <line x1="23" y1="11" x2="17" y2="11"></line>
                </svg>
                Register
              </Link>
              <div className="mobile-theme-toggle">
                <ThemeToggle />
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
